// Google Ads conversion tracking (gtag.js).
//
// The global tag (AW-18382381519) is installed once in web/index.html so
// every SPA route inherits the loader. This module is the *event* layer:
// SPA page_path updates, verified Polar subscription conversions, and Clerk sign-ups.
// The HTML snippet alone is not enough for conversions — Google Ads needs
// either a page-load URL match or a `gtag('event', 'conversion' | 'purchase'
// | 'sign_up')` call at the moment the action happens.
//
// Events are prod-only. Local Vite (`import.meta.env.DEV`) still loads the
// HTML snippet (so tag-assistant diagnosis works) but we do not fire
// conversion events against the live Ads account from a laptop.
//
// Conversion labels come from the Ads UI. VITE_GADS_PURCHASE_SEND_TO
// selects the subscription acquisition action, and VITE_GADS_SIGNUP_SEND_TO
// optionally selects the signup action. Named events alone do not replace
// configuring the matching conversion action in Google Ads.
//
// Dedup: paid orders use sessionStorage; signups use localStorage with
// sessionStorage fallback. Keys stay in-browser and identify each action, so
// React StrictMode remounts and a refresh of /billing/success do not
// double-count. Google Ads also dedups on transaction_id for purchases.

import { reportAccountSignup, signupAlreadySent, markSignupSent } from "./signup-conversion";
import { reportSubscriptionPurchase } from "./subscription-conversion";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

/** Google Ads account tag. Must match the snippet in web/index.html. */
export const GADS_ID = "AW-18382381519";

const PURCHASE_SEND_TO = import.meta.env.VITE_GADS_PURCHASE_SEND_TO as
  | string
  | undefined;
const SIGNUP_SEND_TO = import.meta.env.VITE_GADS_SIGNUP_SEND_TO as
  | string
  | undefined;

const SENT_PREFIX = "gads_sent:";

type Gtag = {
  (command: "js", date: Date): void;
  (command: "config", targetId: string, config?: Record<string, unknown>): void;
  (command: "event", eventName: string, params?: Record<string, unknown>): void;
  (command: "set", field: string, value: Record<string, unknown>): void;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
  }
}

function isAdsEnabled(): boolean {
  return import.meta.env.PROD;
}

// Split per command rather than spreading the union: a spread of a union of
// tuples can't select an overload, so `window.gtag(...args)` fails to resolve.
function callGtag(
  command: "event" | "set" | "config",
  target: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  if (command === "set") {
    gtag("set", target, params ?? {});
  } else if (command === "config") {
    gtag("config", target, params);
  } else {
    gtag("event", target, params);
  }
}

function alreadySent(key: string): boolean {
  try {
    return sessionStorage.getItem(`${SENT_PREFIX}${key}`) === "1";
  } catch {
    return false;
  }
}

function markSent(key: string): void {
  try {
    sessionStorage.setItem(`${SENT_PREFIX}${key}`, "1");
  } catch {
    // Private browsing can throw; better to risk a duplicate than to
    // drop the conversion on the floor.
  }
}

/**
 * SPA virtual pageview. The HTML `gtag('config')` already sent the
 * initial hit, so callers must skip the first client-side mount.
 */
export function trackPageView(path: string): void {
  if (!isAdsEnabled()) return;
  callGtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GADS_ID,
  });
}

export function trackPurchase(params: import("../../../src/billing/checkout-purchase").VerifiedCheckoutPurchase): void {
  if (!isAdsEnabled() || typeof window.gtag !== "function") return;
  reportSubscriptionPurchase(params, {
    tagId: GADS_ID,
    sendTo: PURCHASE_SEND_TO,
    emit: (name, fields) => callGtag("event", name, fields),
    alreadySent,
    markSent,
  });
}

export function trackSignUp(params: { userId: string; createdAt: number }): void {
  if (!isAdsEnabled() || typeof window.gtag !== "function") return;
  reportAccountSignup(params, {
    now: Date.now(),
    tagId: GADS_ID,
    sendTo: SIGNUP_SEND_TO,
    emit: (name, fields) => callGtag("event", name, fields),
    alreadySent: (key) => signupAlreadySent(key, () => localStorage, () => sessionStorage),
    markSent: (key) => markSignupSent(key, () => localStorage, () => sessionStorage),
  });
}

/** Mount once under BrowserRouter. Skips the first hit (HTML config). */
export function useGtagPageViews(): void {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
}

/**
 * Fires `sign_up` when Clerk reports a user whose account was created
 * within five minutes. Persistent dedup prevents repeat visits or later
 * logins from counting again. Mount anywhere inside ClerkProvider.
 */
export function useGtagSignUp(): void {
  const { isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLoaded || !user) return;
    const createdAt = user.createdAt
      ? new Date(user.createdAt).getTime()
      : 0;
    trackSignUp({
      userId: user.id,
      createdAt,
    });
  }, [isLoaded, user]);
}
